import React, { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { isUndefined } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ClientRow } from '../Clients/ClientRow/ClientRow';
import { ClientsTableMessage } from '../Clients/ClientsTableMessage';
import { QuestionBubble } from '../Clients/QuestionBubble';
import { MAXIMUM_BULK_MESSAGE_COUNT } from '../../pages/ClientsPage/constants';
import { getCurrentUser } from '../../selectors/userSelectors';
import { ClientSummaryNode } from '../../generated/webapp_gql';
import { ReactComponent as CheckMark } from '../../assets/svgs/checkmark.svg';
import { ReactComponent as IconSort } from '../../assets/svgs/icon-sort.svg';
import HPICoverageQuestionBubble from './HPICoverageQuestionBubble/HPICoverageQuestionBubble';
import MatchStatusQuestionBubble from './MatchStatusQuestionBubble/MatchStatusQuestionBubble';
import MedicationManagementQuestionBubble from './MedicationManagementQuestionBubble/MedicationManagementQuestionBubble';

interface Props {
  shouldAddColumnForMemberDrawer: boolean;
  tableRows: Array<ClientSummaryNode>;
  selectedMembers: Array<ClientSummaryNode>;
  onSelectMember: (member: ClientSummaryNode) => void;
  onSelectAllClientsOnPage: () => void;
  selectAllIsChecked: boolean;
  selectedMemberUuid?: string;
  matchedFilter: string;
  searchFilter: string;
  onClearAllMembers: () => void;
  onSelectRow: (memberUuid: string) => void;
  onSelectChat: (e: MouseEvent, memberUuid: string) => void;
  onReportVisit: (e: MouseEvent, memberUuid: string) => void;
  orderBy: string;
  setOrderBy: (next: string) => void;
}

export enum ClientsTableTestIDs {
  SelectAllCheckBox = 'SelectAllCheckBox',
  ClientNameColumn = 'ClientNameColumn',
  HPICoverageColumn = 'HPICoverageColumn',
  PAMMCoverageColumn = 'PAMMCoverageColumn',
  NameSortButton = 'NameSortButton',
  MatchStatusColumn = 'MatchStatusColumn',
  BookedSessionsColumn = 'BookedSessionsColumn',
  CompanyColumn = 'CompanyColumn',
  CompanySortButton = 'CompanySortButton',
  RemainingSessionsColumn = 'RemainingSessionsColumn',
  Who5ScoreColumn = 'Who5ScoreColumn',
  AppInstalledColumn = 'AppInstalledColumn',
  ActionsColumn = 'ActionsColumn',
  LastActiveColumn = 'LastActiveColumn',
  LastLoginColumn = 'LastLoginColumn',
}

export const ClientsTable: React.FC<Props> = ({
  shouldAddColumnForMemberDrawer,
  tableRows,
  selectedMembers,
  selectedMemberUuid,
  searchFilter,
  onSelectRow,
  onSelectChat,
  onReportVisit,
  onSelectAllClientsOnPage,
  selectAllIsChecked,
  onSelectMember,
  onClearAllMembers,
  orderBy,
  setOrderBy,
}) => {
  const { showHpiClientStatusInfo } = useFlags();
  const currentUser = useSelector(getCurrentUser);
  const providerIsPsychiatrist = currentUser.isPsychiatrist;

  const handleSortByCompany = () => {
    if (orderBy === '-company') {
      setOrderBy('');
      return;
    }

    if (orderBy === 'company') {
      setOrderBy('-company');
      return;
    }
    setOrderBy('company');
  };

  const handleSortByClientName = () => {
    if (orderBy === 'firstname') {
      setOrderBy('-firstname');
      return;
    }

    if (orderBy === '-firstname') {
      setOrderBy('');
      return;
    }
    setOrderBy('firstname');
  };

  return (
    <>
      <div className="mt-3 overflow-y-auto flex-grow">
        <table className="bg-white w-full border-collapse ">
          <thead>
            <tr>
              <th className="th-clients">
                <div
                  data-testid={ClientsTableTestIDs.SelectAllCheckBox}
                  onClick={onSelectAllClientsOnPage}
                  className={`h-4 w-4 ${
                    selectAllIsChecked ? 'bg-blue-200' : 'border border-gray-400 bg-white'
                  } rounded flex items-center justify-center`}
                >
                  {selectAllIsChecked ? <CheckMark className="h-3 w-3" /> : null}
                </div>
              </th>
              <th className="th-clients">
                <div
                  className="flex flex-row items-center"
                  data-testid={ClientsTableTestIDs.ClientNameColumn}
                >
                  Client
                  <span
                    className={`${
                      orderBy.includes('firstname') ? 'bg-blue-200' : ''
                    } rounded ml-1 p-.5`}
                  >
                    <IconSort
                      onClick={handleSortByClientName}
                      data-testid={ClientsTableTestIDs.NameSortButton}
                      className={`${
                        orderBy.includes('firstname') ? 'text-white' : 'text-gray-600'
                      } h-3 w-3 fill-current`}
                    />
                  </span>
                </div>
              </th>
              <th className="th-clients pl-0" data-testid={ClientsTableTestIDs.CompanyColumn}>
                <div className="flex flex-row items-center">
                  Company
                  <span
                    data-testid={ClientsTableTestIDs.CompanySortButton}
                    className={`${
                      orderBy.includes('company') ? 'bg-blue-200' : ''
                    } rounded ml-1 p-.5`}
                  >
                    <IconSort
                      onClick={handleSortByCompany}
                      className={`${
                        orderBy.includes('company') ? 'text-white' : 'text-gray-600'
                      } h-3 w-3 fill-current`}
                    />
                  </span>
                </div>
              </th>

              <th className="th-clients" data-testid={ClientsTableTestIDs.MatchStatusColumn}>
                <div className="flex items-center">
                  Match Status
                  <MatchStatusQuestionBubble />
                </div>
              </th>

              <th className="th-clients" data-testid={ClientsTableTestIDs.BookedSessionsColumn}>
                Booked
              </th>

              {!providerIsPsychiatrist && (
                <th
                  className="th-clients"
                  data-testid={ClientsTableTestIDs.RemainingSessionsColumn}
                >
                  Sessions Left
                </th>
              )}

              {showHpiClientStatusInfo && (
                <th className="th-clients" data-testid={ClientsTableTestIDs.HPICoverageColumn}>
                  <div className="flex items-center">
                    HPI Coverage
                    <HPICoverageQuestionBubble />
                  </div>
                </th>
              )}

              <th className="th-clients" data-testid={ClientsTableTestIDs.PAMMCoverageColumn}>
                <div className="flex items-center">
                  Medication management
                  <MedicationManagementQuestionBubble />
                </div>
              </th>

              <th className="th-clients">
                <div
                  className="flex flex-row items-center"
                  data-testid={ClientsTableTestIDs.Who5ScoreColumn}
                >
                  WHO-5
                  <QuestionBubble
                    popoverTitle="Wellness Score (WHO-5)"
                    sections={[
                      {
                        sectionBody:
                          'The WHO-5 score consists of questions that clients answer through a questionnaire to self-report on their mental wellbeing. This shows the most recently completed WHO-5 score.',
                      },
                    ]}
                  />
                </div>
              </th>

              <th className="th-clients pl-0" data-testid={ClientsTableTestIDs.ActionsColumn}>
                Actions
              </th>

              <th className="th-clients pl-0" />

              {shouldAddColumnForMemberDrawer && <th className="th-clients w-100" />}
            </tr>

            {selectedMembers.length > 0 && (
              <tr>
                <th colSpan={100} className="sticky top-12">
                  <div className="bg-gray-300 whitespace-pre font-normal rounded p-2 mb-3 ">
                    {selectedMembers.length > 1 ? (
                      selectedMembers.length >= MAXIMUM_BULK_MESSAGE_COUNT ? (
                        <>
                          You have selected the maximum of{' '}
                          <span className="font-bold">{selectedMembers.length}</span> clients.
                        </>
                      ) : (
                        <>
                          <span className="font-bold">{selectedMembers.length}</span> clients are
                          currently selected.
                        </>
                      )
                    ) : (
                      <>
                        <span className="font-bold">{selectedMembers.length}</span> client is
                        currently selected.
                      </>
                    )}

                    <button
                      className="text-blue-400 text-xs background-transparent uppercase px-1 py-1 underline outline-none focus:outline-none ease-linear transition-all duration-150"
                      onClick={onClearAllMembers}
                      type="button"
                    >
                      Clear All
                    </button>
                  </div>
                </th>
              </tr>
            )}
          </thead>

          <tbody>
            {tableRows.map((client) => {
              const selectedMember = selectedMembers.find(
                ({ memberUuid }) => memberUuid === client.memberUuid,
              );

              return (
                <ClientRow
                  isCurrentlySelected={selectedMemberUuid === client.memberUuid}
                  isNewMatch={client.matchStatus === 'NewMatch'}
                  isSelectedForBulkMessaging={!isUndefined(selectedMember)}
                  key={client.memberUuid}
                  matched={client.matchStatus !== 'Unmatched'}
                  client={client}
                  providerIsTherapist={currentUser.isTherapist}
                  providerIsPsychiatrist={providerIsPsychiatrist}
                  onSelectChat={(e) => onSelectChat(e, client.memberUuid)}
                  onSelectMember={() => onSelectMember(client)}
                  onSelectRow={() => onSelectRow(client.memberUuid)}
                  onReportVisit={(e) => onReportVisit(e, client.memberUuid)}
                  shouldAddColumnForMemberDrawer={shouldAddColumnForMemberDrawer}
                  showSelectBox
                />
              );
            })}
          </tbody>
        </table>

        {tableRows.length === 0 && <ClientsTableMessage searchFilter={searchFilter} />}
      </div>
    </>
  );
};
